import axios, { AxiosResponse } from 'axios';

// Get API URL from runtime environment if available, otherwise from build-time env variable
const getApiUrl = (): string => {
  if (window.env && window.env.REACT_APP_API_URL) {
    return window.env.REACT_APP_API_URL;
  }
  return process.env.REACT_APP_API_URL || 'https://tcmf-generator.deplide.org';
};

const API_BASE_URL = getApiUrl();

/**
 * API client for the TCMF Generator Service
 */
export class TcmfGeneratorApi {
  /**
   * Convert Excel file to JSON TCMF messages
   * @param file Excel file containing scenarios
   * @param pretty Whether to format the JSON for readability
   * @returns Generated TCMF messages as JSON string
   */
  async convertToJson(file: File, pretty?: boolean): Promise<string> {
    const formData = new FormData();
    formData.append('content', file);

    const url = pretty !== undefined
        ? `${API_BASE_URL}/toJson?pretty=${pretty}`
        : `${API_BASE_URL}/toJson`;

    try {
      const response: AxiosResponse<string> = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error converting to JSON:', error);
      throw error;
    }
  }

  /**
   * Convert Excel file to Markdown TCMF messages
   * @param file Excel file containing scenarios
   * @param title Optional title for the markdown document
   * @returns Generated TCMF messages as Markdown
   */
  async convertToMarkdown(file: File, title?: string): Promise<string> {
    const formData = new FormData();
    formData.append('content', file);

    const url = title !== undefined
        ? `${API_BASE_URL}/toMarkdown?title=${encodeURIComponent(title)}`
        : `${API_BASE_URL}/toMarkdown`;

    try {
      const response: AxiosResponse<string> = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'text',
      });

      return response.data;
    } catch (error) {
      console.error('Error converting to Markdown:', error);
      throw error;
    }
  }

  /**
   * Download Excel template for creating TCMF scenarios
   * @returns Promise that resolves to a Blob containing the template
   */
  async getTemplate(): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await axios.get(`${API_BASE_URL}/template`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      console.error('Error getting template:', error);
      throw error;
    }
  }

  /**
   * Check if the service is alive
   * @returns Promise that resolves to true if the service is alive
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response: AxiosResponse<string> = await axios.get(`${API_BASE_URL}/fluff`, {
        responseType: 'text',
      });
      return response.data === 'Found fluff';
    } catch (error) {
      console.error('Error checking service health:', error);
      return false;
    }
  }
}

// Create a singleton instance
export const tcmfApi = new TcmfGeneratorApi();
